import { useState, useEffect } from "react";

const DISMISSED_KEY  = "vorsia-pwa-dismissed";
const INSTALLED_KEY  = "vorsia-pwa-installed";
const VISIT_KEY      = "vorsia-pwa-visits";
const SNOOZE_KEY     = "vorsia-pwa-snoozed-until";
const SNOOZE_DAYS    = 3; // re-show after 3 days if snoozed

export default function PWAPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [show,           setShow]           = useState(false);
  const [installing,     setInstalling]     = useState(false);
  const [installed,      setInstalled]      = useState(false);

  useEffect(() => {
    // Don't show if already installed
    if (localStorage.getItem(INSTALLED_KEY) === "true") return;

    // Track visit count
    const visits = parseInt(localStorage.getItem(VISIT_KEY) || "0") + 1;
    localStorage.setItem(VISIT_KEY, visits);

    // Don't show on first visit — let browser show native prompt first
    if (visits < 2) return;

    // Don't show if permanently dismissed
    if (localStorage.getItem(DISMISSED_KEY) === "true") return;

    // Don't show if snoozed
    const snoozedUntil = localStorage.getItem(SNOOZE_KEY);
    if (snoozedUntil && new Date() < new Date(snoozedUntil)) return;

    // Capture the beforeinstallprompt event
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      // Show our custom prompt after a short delay
      setTimeout(() => setShow(true), 2000);
    };

    window.addEventListener("beforeinstallprompt", handler);

    // Detect if already installed via display-mode
    if (window.matchMedia("(display-mode: standalone)").matches) {
      localStorage.setItem(INSTALLED_KEY, "true");
      return;
    }

    // Also listen for successful install
    window.addEventListener("appinstalled", () => {
      localStorage.setItem(INSTALLED_KEY, "true");
      setShow(false);
      setInstalled(true);
      setTimeout(() => setInstalled(false), 3000);
    });

    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    setInstalling(true);
    try {
      await deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === "accepted") {
        localStorage.setItem(INSTALLED_KEY, "true");
        setShow(false);
      } else {
        // User declined native prompt — snooze our prompt
        snooze();
      }
    } catch(e) { console.error(e); }
    finally { setInstalling(false); setDeferredPrompt(null); }
  };

  const snooze = () => {
    const until = new Date();
    until.setDate(until.getDate() + SNOOZE_DAYS);
    localStorage.setItem(SNOOZE_KEY, until.toISOString());
    setShow(false);
  };

  const dismiss = () => {
    localStorage.setItem(DISMISSED_KEY, "true");
    setShow(false);
  };

  // Success toast after install
  if (installed) return (
    <div style={{ position:"fixed", bottom:24, left:"50%", transform:"translateX(-50%)", zIndex:600, background:"rgba(74,222,128,0.95)", color:"#0a0a0f", padding:"12px 24px", borderRadius:12, fontSize:13, fontWeight:700, fontFamily:"'DM Sans',sans-serif", boxShadow:"0 8px 32px rgba(0,0,0,0.3)", animation:"slideDown 0.3s ease", whiteSpace:"nowrap" }}>
      ✓ Vorsia installed successfully!
    </div>
  );

  if (!show) return null;

  return (
    <>
      {/* Backdrop blur on mobile */}
      <div style={{ position:"fixed", inset:0, zIndex:598, background:"rgba(0,0,0,0.4)", backdropFilter:"blur(2px)" }}
        onClick={snooze} />

      {/* Prompt card */}
      <div style={{ position:"fixed", bottom:24, left:"50%", transform:"translateX(-50%)", zIndex:599, width:"calc(100% - 32px)", maxWidth:420, background:"var(--bg-secondary)", border:"1px solid rgba(123,111,232,0.3)", borderRadius:20, padding:"20px 20px 16px", boxShadow:"0 16px 48px rgba(0,0,0,0.5)", animation:"fadeUp 0.4s ease", fontFamily:"'DM Sans',sans-serif" }}>

        {/* Header */}
        <div style={{ display:"flex", gap:14, alignItems:"center", marginBottom:14 }}>
          <img src="/vorsia_icon_192.png" alt="Vorsia" style={{ width:52, height:52, borderRadius:14, flexShrink:0 }} />
          <div>
            <div style={{ fontSize:15, fontWeight:700, color:"var(--text-primary)", marginBottom:3 }}>Add Vorsia to your home screen</div>
            <div style={{ fontSize:12, color:"var(--text-secondary)", lineHeight:1.5 }}>Get faster access with the full app experience — no browser needed.</div>
          </div>
          <button onClick={snooze} style={{ background:"none", border:"none", color:"var(--text-tertiary)", cursor:"pointer", fontSize:18, flexShrink:0, padding:4, alignSelf:"flex-start" }}>✕</button>
        </div>

        {/* Features */}
        <div style={{ display:"flex", gap:16, marginBottom:16, padding:"10px 14px", background:"var(--bg-card)", borderRadius:10 }}>
          {[
            { i:"⚡", l:"Instant load" },
            { i:"🔔", l:"Job alerts"   },
            { i:"📴", l:"Works offline"},
          ].map(f => (
            <div key={f.l} style={{ flex:1, textAlign:"center" }}>
              <div style={{ fontSize:18, marginBottom:3 }}>{f.i}</div>
              <div style={{ fontSize:10, color:"var(--text-tertiary)" }}>{f.l}</div>
            </div>
          ))}
        </div>

        {/* Buttons */}
        <div style={{ display:"flex", gap:8 }}>
          <button onClick={handleInstall} disabled={installing}
            style={{ flex:2, background:"var(--accent)", border:"none", color:"#fff", padding:"12px", borderRadius:10, fontWeight:700, fontSize:14, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", gap:8, opacity:installing?0.8:1 }}>
            {installing ? (
              <><div style={{ width:14, height:14, border:"2px solid rgba(255,255,255,0.3)", borderTopColor:"#fff", borderRadius:"50%", animation:"spin 0.7s linear infinite" }} />Installing…</>
            ) : "⬇ Install App"}
          </button>
          <button onClick={snooze}
            style={{ flex:1, background:"var(--bg-hover)", border:"1px solid var(--border)", color:"var(--text-secondary)", padding:"12px", borderRadius:10, fontSize:13, cursor:"pointer" }}>
            Later
          </button>
          <button onClick={dismiss}
            style={{ background:"var(--bg-hover)", border:"1px solid var(--border)", color:"var(--text-tertiary)", padding:"12px 14px", borderRadius:10, fontSize:12, cursor:"pointer" }}>
            Never
          </button>
        </div>
      </div>
    </>
  );
}
